/**
 * Playbooks send PRD, Unit U9 (R10 / Key Technical Decision 10) — the DOM-free
 * half of the cursor-aware token-insert picker.
 *
 * ── WHY THIS MODULE IS DOM-FREE ───────────────────────────────────────────────
 * U9 is three files on purpose: this one (pure string/lookup logic, unit
 * tested under `bun:test`, which runs with no `jsdom`), `use-caret-tracker.ts`
 * (the per-field caret/Range bookkeeping, which needs real DOM), and
 * `components/TokenPicker.tsx` (the Glaze popover). Keeping the splice and the
 * row-value rules here means the behavior a rep actually cares about — the
 * token lands where the cursor was, and the picker tells the truth about what
 * each token will resolve to — is testable without a browser.
 *
 * Frontend-safe: no zod, no core imports; the token vocabulary comes from
 * `_shared/launcher-copy-tokens.ts`, the one source of truth launcher and
 * Playbooks share (see that module's header).
 */
import {
  CLAY_OWNED_TOKEN_SHORT_CAPTION,
  COPY_VARIABLE_FIELDS,
  isClayOwnedToken,
  type CopyVariableField,
} from '../_shared/launcher-copy-tokens';

/**
 * Splice `token` into `text` at `caret`.
 *
 * `caret` is whatever the caret tracker last recorded for the target field,
 * which is deliberately allowed to be `null` (the rep opened the picker
 * without ever having put a cursor in the field) or stale (the field's text
 * shrank since — an AI regeneration, a discard). Both are ordinary states, not
 * errors, so this clamps into `[0, text.length]` rather than throwing or
 * refusing: `null` appends at the end, an over-long caret appends at the end,
 * a negative caret prepends.
 *
 * The returned `caret` is the position just after the inserted token — the
 * caller restores it so the rep can keep typing where they left off instead of
 * being bounced to the end of the field (launcher's `insertVariable`
 * append-only bug, `mini-apps/launcher/components/CopyEditor.tsx`, is exactly
 * what this exists to avoid).
 */
export function insertTokenAt(
  text: string,
  caret: number | null,
  token: string,
): { text: string; caret: number } {
  const at = caret === null ? text.length : Math.min(Math.max(caret, 0), text.length);
  return { text: text.slice(0, at) + token + text.slice(at), caret: at + token.length };
}

/**
 * Which of a step's two copy fields one Tokens insert lands in — and, by
 * construction, the field KEYS the hosts call `useCaretTracker<TokenTargetField>()`
 * with, so `tracker.insert(target, …)` needs no second mapping table.
 */
export type TokenTargetField = 'subject' | 'body';

/**
 * CANONICAL "why" for the one-Tokens-button rule (prod QA, 2026-09-16, Fix 2).
 *
 * There is ONE Tokens button per step — two pickers read as one, so every
 * token landed in the Subject. The one button inserts into the field the rep
 * was last in, and Body until they have touched either (Body holds the bulk of
 * the copy and the button sits under it). `lastField` comes from
 * `use-caret-tracker.ts`'s recorded focus/keyup/mouseup/blur events, never from
 * `document.activeElement` — the button's own click has already blurred the
 * field by the time it lands.
 *
 * Codex fixes 2026-09-17, F8 — `isReplyStep` short-circuits to `'body'`.
 * Both editors hide the Subject field on a Gong reply step, but
 * `useGongReplyStepOrders` answers "no reply steps" until the flow's steps
 * load, so a Subject can be focused (recording `lastField: 'subject'`) and
 * THEN disappear; the picker would keep writing into the field the rep can
 * no longer see. The rule lives here, not in each caller, so the two editors
 * cannot drift.
 */
export function resolveTokenTarget(lastField: TokenTargetField | null, isReplyStep = false): TokenTargetField {
  if (isReplyStep) return 'body';
  return lastField ?? 'body';
}

/**
 * The target field's name as the picker's "Insert into …" header spells it, so
 * a rep sees where a token is about to go BEFORE clicking a row rather than
 * discovering it afterwards.
 */
export function tokenTargetLabel(target: TokenTargetField): string {
  return target === 'subject' ? 'Subject' : 'Body';
}

/** What a picker row shows on its right-hand side, and in which tone. */
export interface TokenRowValue {
  value: string;
  tone: 'filled' | 'empty' | 'neutral';
}

/**
 * The right-hand "resolves to…" cell for one picker row (decision 4 — a live
 * resolved-value preview per row, cut down to token + value and nothing else).
 *
 * The four cases, in the order they are checked — the order matters, because
 * more than one can be true of the same row at once:
 *
 * 0. **A {@link CLAY_OWNED_TOKENS} row** → `'Set by Clay at send'`, neutral.
 *    FIRST, ahead of everything else, because all three of the states below
 *    are also true of these rows at various times and none of them is the
 *    useful answer: `Company Name` has no sample in any mode (it would read
 *    `'Empty'` forever), and the two sender tokens DO have a Terra sample —
 *    which is precisely the value that must not be shown here, since Clay's
 *    "Resolve sender" may pick a different sender. Terra's guess is still
 *    shown in the PREVIEW (as a distinctly-styled `deferred` chip that says so)
 *    because there it sits next to a caption explaining it; a bare value in a
 *    picker cell has nowhere to carry that caveat, so the row states the owner
 *    instead of the value.
 * 1. **A Sender token with no sender on the sequence** → `'Per segmentation'`,
 *    neutral. A token that WILL resolve once a sender is set is not the same
 *    thing as a token that is empty for this member, and flagging it warning-
 *    orange (as `'Empty'` does) sends the rep hunting for a problem that does
 *    not exist. Mirrors the prototype's `neutral` branch (`tokenTrigger()`).
 * 2. **No sample at all** (`vars === null` — library mode, where there is no
 *    campaign member to resolve against) → `'No sample'`, neutral. Again not
 *    "empty": nothing has been evaluated.
 * 3. Otherwise the sample decides: a non-empty `vars[field.label]` is the
 *    resolved value (`filled`), and absent-or-blank is `'Empty'` (`empty`,
 *    the warning tone) — the same "absent or `''` both count as a gap" rule
 *    `resolveCopy`/`checkCopyVarGapsByMemberId` use, so a token this picker
 *    shows as filled can never turn out to be a send-time gap.
 *
 * ── THE HISTORY OF CASE 0, WHICH IS NOT THE SAME CASE ─────────────────────
 * A row state reading `'Resolved by Clay'` used to sit here, backed by the old
 * `CLAY_RESOLVED_VARS` pass-through set, and was DELETED in U1 because that
 * set had no downstream guarantee: nothing in the V2 Clay workflow filled the
 * literals and Gong truncated them at the first space (verified live
 * 2026-09-15 — `{{First Name}}` renders as `{{First}}`, in red).
 *
 * Case 0 above is not that state coming back. The set is different (three
 * tokens, not four; `First Name` stays Terra-resolved and shows its real
 * sample), and the reason is different: Clay's V3 `Render copy` node is
 * contractually responsible for exactly `CLAY_OWNED_TOKENS` and hard-stops
 * the send if any brace survives. The wording differs too, deliberately —
 * "Set by Clay at send" names WHEN, which is the part a rep needs, where
 * "Resolved by Clay" claimed a thing that was not happening.
 *
 * Keyed by `field.label`, not `field.variable`: `buildTemplateVars` is keyed by
 * the bare token name (`'Account Name'`), while `variable` carries the braces.
 */
export function tokenRowValue(
  field: CopyVariableField,
  vars: Record<string, string> | null,
  sender: string | null,
): TokenRowValue {
  if (isClayOwnedToken(field.label)) {
    return { value: CLAY_OWNED_TOKEN_SHORT_CAPTION, tone: 'neutral' };
  }
  if (field.object === 'Sender' && sender === null) {
    return { value: 'Per segmentation', tone: 'neutral' };
  }
  if (vars === null) return { value: 'No sample', tone: 'neutral' };
  const resolved = vars[field.label] ?? '';
  return resolved === '' ? { value: 'Empty', tone: 'empty' } : { value: resolved, tone: 'filled' };
}

/**
 * The picker's search: the rows of {@link COPY_VARIABLE_FIELDS} whose object,
 * label, or braced variable text contains `query`, case-insensitively, in
 * vocabulary order.
 *
 * Three haystacks rather than just the label, because reps arrive from three
 * directions: typing the field name (`account`), typing the group they want to
 * browse (`sender` → all three sender tokens at once), and pasting a
 * `{{Token}}` they saw in existing copy (which only matches once the braces are
 * part of the haystack).
 *
 * An empty or whitespace-only query returns the full vocabulary — the panel
 * opens showing everything, not nothing.
 */
export function filterTokenFields(query: string): CopyVariableField[] {
  const needle = query.trim().toLowerCase();
  if (needle === '') return COPY_VARIABLE_FIELDS;
  return COPY_VARIABLE_FIELDS.filter((field) =>
    `${field.object} ${field.label} ${field.variable}`.toLowerCase().includes(needle),
  );
}
